// Strategy Framework Library — canonical 60+ frameworks with structured templates.
// Each framework has: id, name, category, useCase, whenToUse, inputs (prompts), output (what to write).

export const frameworkCategories = [
  { id: "portfolio", label: "Portfolio & Corporate" },
  { id: "competitive", label: "Competitive Strategy" },
  { id: "growth", label: "Growth & Markets" },
  { id: "innovation", label: "Innovation & Product" },
  { id: "ops", label: "Operations & Quality" },
  { id: "people", label: "People & Org" },
  { id: "exec", label: "Execution & Performance" },
  { id: "macro", label: "Macro & Risk" },
  { id: "customer", label: "Customer & Marketing" },
  { id: "transform", label: "Transformation & Digital" },
  { id: "finance", label: "Finance & Capital" },
  { id: "esg", label: "ESG & Sustainability" },
];

// Each input is a labelled prompt; the user writes one paragraph per prompt.
export const frameworks = [
  {
    id: "bcg-growth-share",
    name: "BCG Growth-Share Matrix",
    category: "portfolio",
    useCase: "Portfolio prioritisation across business units",
    whenToUse: "Deciding where to invest, divest, or harvest across multiple products/units.",
    inputs: [
      { id: "stars", label: "Stars (high growth · high share)", prompt: "List BUs with high market growth and high relative share. Invest." },
      { id: "cash-cows", label: "Cash cows (low growth · high share)", prompt: "List BUs that generate cash. Harvest and reinvest." },
      { id: "question-marks", label: "Question marks (high growth · low share)", prompt: "List BUs that need a build-or-divest decision." },
      { id: "dogs", label: "Dogs (low growth · low share)", prompt: "List BUs to consider divesting." },
    ],
    output: "A 1-page portfolio decision per BU: invest / hold / harvest / divest, with a reason and an owner.",
  },
  {
    id: "ge-mckinsey",
    name: "GE-McKinsey Matrix",
    category: "portfolio",
    useCase: "Nuanced portfolio analysis across multiple criteria",
    whenToUse: "Multi-business corporations evaluating SBU strategic position.",
    inputs: [
      { id: "industry-attractiveness", label: "Industry attractiveness factors", prompt: "Score growth, profitability, competitive intensity, regulation, etc." },
      { id: "business-strength", label: "Business unit strength factors", prompt: "Score share, brand, cost position, capabilities." },
      { id: "placement", label: "9-cell placement", prompt: "Place each SBU in one of nine cells. Identify invest / selective / harvest actions." },
    ],
    output: "Nine-cell heat map with explicit invest / selective / harvest call per SBU.",
  },
  {
    id: "ansoff",
    name: "Ansoff Matrix",
    category: "growth",
    useCase: "Selecting growth strategy",
    whenToUse: "Identifying paths to grow revenue.",
    inputs: [
      { id: "market-penetration", label: "Market penetration (existing products · existing markets)", prompt: "How will you sell more to current customers in current markets?" },
      { id: "market-development", label: "Market development (existing products · new markets)", prompt: "Which new geographies or segments fit current products?" },
      { id: "product-development", label: "Product development (new products · existing markets)", prompt: "Which adjacent products serve current customers?" },
      { id: "diversification", label: "Diversification (new products · new markets)", prompt: "Where would you go if you accepted highest risk for highest growth?" },
    ],
    output: "Four named growth bets with effort, expected payoff, and risk.",
  },
  {
    id: "value-chain",
    name: "Value Chain Analysis",
    category: "competitive",
    useCase: "Identifying sources of competitive advantage",
    whenToUse: "Cost optimisation, differentiation strategy, operational excellence.",
    inputs: [
      { id: "primary", label: "Primary activities", prompt: "Inbound logistics · operations · outbound logistics · marketing & sales · service. Where is value created?" },
      { id: "support", label: "Support activities", prompt: "Firm infrastructure · HR · technology · procurement. Where is the leverage?" },
      { id: "advantages", label: "Sources of advantage", prompt: "Which 1-2 activities are the actual moat? Where can you afford to be average?" },
    ],
    output: "Annotated value-chain diagram + 3 explicit cost or differentiation bets.",
  },
  {
    id: "core-competencies",
    name: "Core Competencies (Hamel & Prahalad)",
    category: "competitive",
    useCase: "Defining what the company does uniquely well",
    whenToUse: "Strategic planning, M&A decisions, resource allocation.",
    inputs: [
      { id: "candidates", label: "Candidate competencies", prompt: "List 5-7 things the company does well today." },
      { id: "tests", label: "3 tests", prompt: "For each: (1) provides customer value? (2) hard to imitate? (3) opens future markets?" },
      { id: "core", label: "True core competencies", prompt: "Keep only those that pass all three tests." },
    ],
    output: "2-3 explicit core competencies and the activities that should be protected / divested accordingly.",
  },
  {
    id: "vrio",
    name: "VRIO Framework",
    category: "competitive",
    useCase: "Evaluating resources for sustained competitive advantage",
    whenToUse: "Internal capability audits, strategic resource allocation.",
    inputs: [
      { id: "valuable", label: "Valuable?", prompt: "Does the resource exploit an opportunity or neutralise a threat?" },
      { id: "rare", label: "Rare?", prompt: "Few competitors have it?" },
      { id: "imitable", label: "Costly to imitate?", prompt: "Hard to copy or substitute?" },
      { id: "organised", label: "Organised to capture?", prompt: "Is the company structured to exploit it?" },
    ],
    output: "VRIO score per resource → competitive parity / temporary / sustained advantage.",
  },
  {
    id: "porters-generic",
    name: "Porter's Generic Strategies",
    category: "competitive",
    useCase: "Choosing competitive positioning",
    whenToUse: "Business unit strategy, market positioning.",
    inputs: [
      { id: "cost-leadership", label: "Cost leadership option", prompt: "What does winning on cost require? Scale, scope, location, technology?" },
      { id: "differentiation", label: "Differentiation option", prompt: "What does winning on uniqueness require? Brand, product, service?" },
      { id: "focus", label: "Focus option", prompt: "Which narrow segment can you dominate?" },
      { id: "decision", label: "Chosen position", prompt: "Pick exactly one. State the trade-offs you accept." },
    ],
    output: "One declared position with the things you'll deliberately not do.",
  },
  {
    id: "blue-ocean",
    name: "Blue Ocean Strategy",
    category: "growth",
    useCase: "Creating uncontested market space",
    whenToUse: "Innovation initiatives, escaping red-ocean competition.",
    inputs: [
      { id: "eliminate", label: "Eliminate", prompt: "Which industry factors can be eliminated entirely?" },
      { id: "reduce", label: "Reduce", prompt: "Which factors should be reduced well below industry standard?" },
      { id: "raise", label: "Raise", prompt: "Which factors should be raised well above industry standard?" },
      { id: "create", label: "Create", prompt: "Which factors should be created that the industry has never offered?" },
    ],
    output: "Strategy canvas + four-actions grid + tagline.",
  },
  {
    id: "pestel",
    name: "PESTEL Analysis",
    category: "macro",
    useCase: "Macro-environmental scanning",
    whenToUse: "Market entry, long-range planning, risk assessment.",
    inputs: [
      { id: "political", label: "Political", prompt: "Government policy, stability, regulation." },
      { id: "economic", label: "Economic", prompt: "Growth, inflation, interest rates, exchange rates." },
      { id: "social", label: "Social", prompt: "Demographics, attitudes, culture, lifestyle." },
      { id: "tech", label: "Technological", prompt: "Innovation, adoption, infrastructure." },
      { id: "environmental", label: "Environmental", prompt: "Climate, sustainability, resource scarcity." },
      { id: "legal", label: "Legal", prompt: "Regulation, compliance, litigation risk." },
    ],
    output: "Six-factor scan with the 3-5 forces that will most shape the next 3 years.",
  },
  {
    id: "balanced-scorecard",
    name: "Balanced Scorecard",
    category: "exec",
    useCase: "Performance measurement and execution",
    whenToUse: "Translating strategy into operational KPIs.",
    inputs: [
      { id: "financial", label: "Financial perspective", prompt: "What financial measures matter most? Revenue, margin, ROIC?" },
      { id: "customer", label: "Customer perspective", prompt: "What customer outcomes? NPS, retention, share of wallet?" },
      { id: "internal", label: "Internal process", prompt: "Which processes must we excel at? Cycle time, quality, innovation rate?" },
      { id: "learning", label: "Learning & growth", prompt: "What capabilities must we build? Skills, culture, technology?" },
    ],
    output: "Cascaded KPI sheet with 3-5 measures per perspective and target / threshold for each.",
  },
  {
    id: "okrs",
    name: "OKRs (Objectives & Key Results)",
    category: "exec",
    useCase: "Goal-setting and alignment",
    whenToUse: "Quarterly/annual planning, cross-functional alignment.",
    inputs: [
      { id: "objective", label: "Objective", prompt: "Qualitative, ambitious, time-bound. What outcome do we want?" },
      { id: "krs", label: "Key results", prompt: "3-5 measurable outcomes that prove the objective is achieved." },
      { id: "owners", label: "Owners + check-ins", prompt: "Who is accountable? What is the cadence?" },
    ],
    output: "Single-page OKR sheet (linked to the dedicated tool at /ops/okr-tracker).",
  },
  {
    id: "scenario-planning",
    name: "Scenario Planning",
    category: "macro",
    useCase: "Preparing for multiple future states",
    whenToUse: "Long-term planning, uncertainty management.",
    inputs: [
      { id: "drivers", label: "Critical uncertainties", prompt: "Pick 2 axes that are most uncertain and most impactful." },
      { id: "scenarios", label: "Four scenarios", prompt: "Describe each quadrant as a vivid 1-paragraph future." },
      { id: "implications", label: "Strategic implications", prompt: "For each scenario: which moves win, which lose, which are robust?" },
      { id: "indicators", label: "Early indicators", prompt: "Signals that tell you which scenario is unfolding." },
    ],
    output: "4 named scenarios + a no-regrets shortlist + a watch-list of leading indicators.",
  },
  {
    id: "game-theory",
    name: "Game Theory Mini-Map",
    category: "competitive",
    useCase: "Modelling competitive interactions",
    whenToUse: "Pricing wars, auctions, negotiation moves.",
    inputs: [
      { id: "players", label: "Players", prompt: "Who are the rational players?" },
      { id: "moves", label: "Moves & payoffs", prompt: "Map options for each player and likely payoffs." },
      { id: "equilibrium", label: "Predicted equilibrium", prompt: "Where does this likely settle? Is there a way to change the game?" },
    ],
    output: "Decision tree or payoff matrix + recommended move with reasoning.",
  },
  {
    id: "evc",
    name: "Economic Value to Customer (EVC)",
    category: "customer",
    useCase: "Value-based pricing",
    whenToUse: "B2B pricing, bundling, sales support.",
    inputs: [
      { id: "ref-price", label: "Reference price", prompt: "What does the next-best alternative cost the customer?" },
      { id: "differentiation", label: "Positive differentiation", prompt: "Quantified value-adds vs the alternative." },
      { id: "negative-diff", label: "Negative differentiation", prompt: "Quantified switching costs / disadvantages." },
      { id: "evc", label: "EVC and price ladder", prompt: "Reference price + positive − negative = EVC. Pick capture %." },
    ],
    output: "EVC waterfall + 2-3 price points with capture rationale.",
  },
  {
    id: "delta-model",
    name: "Delta Model",
    category: "competitive",
    useCase: "Customer bonding strategy",
    whenToUse: "SaaS, platforms, retention strategy.",
    inputs: [
      { id: "best-product", label: "Best product position", prompt: "Functional or low-cost leadership only." },
      { id: "customer-solution", label: "Total customer solution", prompt: "Bundles, services, integration; deeper relationship." },
      { id: "system-lockin", label: "System lock-in", prompt: "Network effects, complementors, standards." },
      { id: "decision", label: "Chosen lock-in mode", prompt: "Which mode best fits the business? What capabilities are needed?" },
    ],
    output: "Single declared lock-in mode + 3 enabling capabilities to invest in.",
  },
  {
    id: "strategic-group-map",
    name: "Strategic Group Mapping",
    category: "competitive",
    useCase: "Understanding competitive clusters",
    whenToUse: "Market positioning, white-space identification.",
    inputs: [
      { id: "axes", label: "Two axes that matter", prompt: "Pick the two strategic dimensions that segment the industry." },
      { id: "groups", label: "Groups", prompt: "Plot competitors into clusters. Note size of each cluster." },
      { id: "white-space", label: "White space", prompt: "Where on the map is no one credibly playing?" },
    ],
    output: "Strategic group map + 1-2 explicit white-space hypotheses.",
  },
  {
    id: "rbv",
    name: "Resource-Based View (RBV)",
    category: "competitive",
    useCase: "Identifying firm-specific resources",
    whenToUse: "Sustained competitive advantage analysis.",
    inputs: [
      { id: "tangible", label: "Tangible resources", prompt: "Plant, equipment, locations, capital." },
      { id: "intangible", label: "Intangible resources", prompt: "Brand, IP, data, relationships." },
      { id: "capabilities", label: "Capabilities", prompt: "Repeatable processes that create value." },
      { id: "advantage", label: "Sustained advantage", prompt: "Which combine to be valuable, rare, hard to imitate, organised?" },
    ],
    output: "Resource map + 2-3 capabilities to deepen.",
  },
  {
    id: "dynamic-capabilities",
    name: "Dynamic Capabilities",
    category: "transform",
    useCase: "Adapting to rapidly changing environments",
    whenToUse: "Digital transformation, disruption response.",
    inputs: [
      { id: "sense", label: "Sensing", prompt: "How does the organisation detect emerging opportunities and threats?" },
      { id: "seize", label: "Seizing", prompt: "How does it commit resources quickly when an opportunity appears?" },
      { id: "transform", label: "Transforming", prompt: "How does it reconfigure assets, processes, structure when needed?" },
    ],
    output: "Maturity rating per capability + the single most important upgrade for the next 12 months.",
  },
  {
    id: "real-options",
    name: "Real Options Analysis",
    category: "finance",
    useCase: "Valuing flexibility in investment",
    whenToUse: "R&D portfolio, staged investments, uncertain entries.",
    inputs: [
      { id: "options", label: "Options identified", prompt: "Defer / expand / abandon / switch / contract." },
      { id: "value-drivers", label: "Value drivers", prompt: "Underlying asset value, volatility, time, exercise cost." },
      { id: "decision", label: "Decision rule", prompt: "Stage gates and trigger conditions to exercise / abandon." },
    ],
    output: "Stage-gated investment plan with explicit kill criteria.",
  },
  {
    id: "design-thinking",
    name: "Design Thinking",
    category: "innovation",
    useCase: "Human-centred problem solving",
    whenToUse: "Product/service innovation, customer experience.",
    inputs: [
      { id: "empathise", label: "Empathise", prompt: "What did you observe? Quotes, journey moments, pain." },
      { id: "define", label: "Define", prompt: "Point-of-view: user · need · insight." },
      { id: "ideate", label: "Ideate", prompt: "Wide list, then short list of solution concepts." },
      { id: "prototype", label: "Prototype & test", prompt: "What did you build, what did you learn, what's next?" },
    ],
    output: "POV statement + 3 prioritised concepts + a learning roadmap.",
  },
  {
    id: "lean-startup",
    name: "Lean Startup / Build-Measure-Learn",
    category: "innovation",
    useCase: "Rapid experimentation",
    whenToUse: "New venture, corporate innovation, MVP testing.",
    inputs: [
      { id: "hypothesis", label: "Riskiest hypothesis", prompt: "Single belief that, if wrong, kills the idea." },
      { id: "experiment", label: "Experiment design", prompt: "What's the cheapest test? What metric proves/disproves?" },
      { id: "result", label: "Result", prompt: "Outcome and decision: persevere / pivot / kill." },
    ],
    output: "Hypothesis log with experiments and decisions.",
  },
  {
    id: "platform-network-effects",
    name: "Platform / Network Effects",
    category: "growth",
    useCase: "Designing multi-sided platforms",
    whenToUse: "Marketplace, ecosystem, platform business models.",
    inputs: [
      { id: "sides", label: "Sides", prompt: "Who are the participants? What do they exchange?" },
      { id: "loops", label: "Network-effect loops", prompt: "Direct, indirect, data, social. Which are present?" },
      { id: "chicken-egg", label: "Cold-start plan", prompt: "Subsidise which side, which geography, for how long?" },
      { id: "moat", label: "Moat", prompt: "What stops a competitor replicating once you reach liquidity?" },
    ],
    output: "Liquidity plan with explicit subsidy budget and competitive moat.",
  },
  {
    id: "flywheel",
    name: "Flywheel Effect (Amazon)",
    category: "growth",
    useCase: "Self-reinforcing growth loops",
    whenToUse: "Growth strategy, identifying virtuous cycles.",
    inputs: [
      { id: "loop", label: "The loop", prompt: "List 5-7 nodes (e.g. low prices → traffic → sellers → selection → low cost → low prices)." },
      { id: "weakest", label: "Weakest node", prompt: "Which node breaks the loop today? What investment fixes it?" },
    ],
    output: "Annotated flywheel diagram + 2-3 investments to accelerate the cycle.",
  },
  {
    id: "moores-chasm",
    name: "Crossing the Chasm",
    category: "innovation",
    useCase: "Tech adoption lifecycle",
    whenToUse: "Disruptive product launches.",
    inputs: [
      { id: "innovators", label: "Innovators / early adopters", prompt: "Who are they? What hooks them?" },
      { id: "chasm", label: "The chasm", prompt: "What stops the early majority? Pragmatism, references, switching cost?" },
      { id: "beachhead", label: "Beachhead segment", prompt: "Which narrow segment can you dominate first?" },
      { id: "expansion", label: "Bowling alley expansion", prompt: "How do you knock down adjacent segments next?" },
    ],
    output: "Beachhead segment + sequenced adjacent-segment plan.",
  },
  {
    id: "s-curve",
    name: "S-Curve Analysis",
    category: "innovation",
    useCase: "Tech and product lifecycle",
    whenToUse: "R&D investment timing, market maturity.",
    inputs: [
      { id: "current-curve", label: "Current S-curve", prompt: "Where is today's tech on the curve?" },
      { id: "next-curve", label: "Next S-curve", prompt: "What will replace it? When?" },
      { id: "transition", label: "Transition plan", prompt: "When and how do you start investing in the next curve?" },
    ],
    output: "Two overlapping S-curves with funded transition plan.",
  },
  {
    id: "bass-diffusion",
    name: "Bass Diffusion Model",
    category: "growth",
    useCase: "Forecasting adoption",
    whenToUse: "New product launch forecasting.",
    inputs: [
      { id: "market", label: "Total addressable adopters (m)", prompt: "Total who could ever adopt." },
      { id: "innovation-coef", label: "Innovation coef (p)", prompt: "Mass-media / external influence rate." },
      { id: "imitation-coef", label: "Imitation coef (q)", prompt: "Word-of-mouth rate." },
      { id: "implications", label: "Forecast implications", prompt: "Peak period, marketing budget shape." },
    ],
    output: "Adoption curve and the period that needs the heaviest marketing investment.",
  },
  {
    id: "nps",
    name: "Net Promoter Score (NPS)",
    category: "customer",
    useCase: "Loyalty measurement",
    whenToUse: "CX strategy, retention, benchmarking.",
    inputs: [
      { id: "score", label: "NPS today", prompt: "% promoters − % detractors." },
      { id: "drivers", label: "Top drivers", prompt: "What promoters love. What detractors flag." },
      { id: "actions", label: "Actions", prompt: "3 changes that would move the score." },
    ],
    output: "NPS dashboard + 3 funded improvement actions.",
  },
  {
    id: "clv",
    name: "Customer Lifetime Value (CLV)",
    category: "customer",
    useCase: "Customer profitability",
    whenToUse: "Marketing ROI, segmentation, loyalty design.",
    inputs: [
      { id: "arpa", label: "ARPA", prompt: "Average revenue per account / period." },
      { id: "gm", label: "Gross margin %", prompt: "Variable cost stripped out." },
      { id: "churn", label: "Churn / month or year", prompt: "%/period." },
      { id: "cac", label: "CAC", prompt: "Blended cost to acquire one customer." },
      { id: "ltv-cac", label: "LTV/CAC + payback", prompt: "Implications for spend mix." },
    ],
    output: "Per-segment LTV, LTV/CAC ratio, and payback period.",
  },
  {
    id: "cohort-analysis",
    name: "Cohort Analysis",
    category: "customer",
    useCase: "Retention and behaviour over time",
    whenToUse: "Subscription, product-market fit.",
    inputs: [
      { id: "cohort-defn", label: "Cohort definition", prompt: "Sign-up week / month, channel, plan." },
      { id: "metric", label: "Retention curve metric", prompt: "% active, % paying, ARPU, etc." },
      { id: "shape", label: "Curve shape", prompt: "Does it flatten? At what level? When?" },
      { id: "actions", label: "Actions", prompt: "Which lifecycle moment is bleeding? What ships first?" },
    ],
    output: "Cohort grid + flagged lifecycle interventions.",
  },
  {
    id: "unit-economics",
    name: "Unit Economics / Contribution Margin",
    category: "finance",
    useCase: "Per-transaction profitability",
    whenToUse: "Business model validation, scaling.",
    inputs: [
      { id: "revenue-per-unit", label: "Revenue per unit / order", prompt: "" },
      { id: "variable-cost", label: "Variable cost per unit", prompt: "COGS, payments, fulfilment, support." },
      { id: "contribution", label: "Contribution per unit", prompt: "Revenue − variable cost." },
      { id: "fixed-overhead", label: "Fixed overhead absorption", prompt: "Units required to cover fixed costs." },
    ],
    output: "Per-unit P&L + scale required for break-even.",
  },
  {
    id: "tam-sam-som",
    name: "TAM / SAM / SOM",
    category: "growth",
    useCase: "Market sizing",
    whenToUse: "Market entry, investor decks.",
    inputs: [
      { id: "tam", label: "TAM (Total addressable market)", prompt: "Top-down + bottom-up estimate." },
      { id: "sam", label: "SAM (Serviceable addressable)", prompt: "Geographies and segments you can plausibly reach." },
      { id: "som", label: "SOM (Serviceable obtainable)", prompt: "Realistic share within 3-5 years." },
      { id: "sensitivity", label: "Sensitivity", prompt: "Re-run with optimistic / pessimistic assumptions." },
    ],
    output: "Triangulated TAM-SAM-SOM with explicit assumptions and sensitivity (linked: /strategy/market-sizing).",
  },
  {
    id: "benchmarking",
    name: "Benchmarking",
    category: "exec",
    useCase: "Performance comparison vs best-in-class",
    whenToUse: "Operational improvement, gap analysis.",
    inputs: [
      { id: "metrics", label: "Metrics & peers", prompt: "Pick 5-10 metrics and 5-7 peers." },
      { id: "us-vs-them", label: "Us vs them", prompt: "Where are we above / below median?" },
      { id: "actions", label: "Closing actions", prompt: "Which 3 gaps will you close in the next 12 months?" },
    ],
    output: "Benchmarking heat map + 3 prioritised improvement initiatives.",
  },
  {
    id: "5-whys",
    name: "5 Whys / Root Cause Analysis",
    category: "ops",
    useCase: "Problem diagnosis",
    whenToUse: "Operational issues, quality problems.",
    inputs: [
      { id: "problem", label: "Problem statement", prompt: "Crisp, specific, observable." },
      { id: "why1", label: "Why 1", prompt: "" },
      { id: "why2", label: "Why 2", prompt: "" },
      { id: "why3", label: "Why 3", prompt: "" },
      { id: "why4", label: "Why 4", prompt: "" },
      { id: "why5", label: "Why 5", prompt: "" },
      { id: "cause-action", label: "Root cause + action", prompt: "True root cause, owner, deadline." },
    ],
    output: "Single-page why-cascade with one owned action.",
  },
  {
    id: "fishbone",
    name: "Fishbone (Ishikawa) Diagram",
    category: "ops",
    useCase: "Identifying causes of complex problems",
    whenToUse: "Process improvement, quality, risk.",
    inputs: [
      { id: "problem", label: "Effect / problem", prompt: "" },
      { id: "people", label: "People", prompt: "" },
      { id: "process", label: "Process", prompt: "" },
      { id: "tech", label: "Technology / equipment", prompt: "" },
      { id: "materials", label: "Materials", prompt: "" },
      { id: "environment", label: "Environment", prompt: "" },
      { id: "measurement", label: "Measurement", prompt: "" },
    ],
    output: "Fishbone with 1-3 likely root causes selected.",
  },
  {
    id: "pareto",
    name: "Pareto (80/20)",
    category: "ops",
    useCase: "Prioritising high-impact areas",
    whenToUse: "Resource allocation, sales focus.",
    inputs: [
      { id: "items", label: "Items ranked by impact", prompt: "List with quantified contribution." },
      { id: "vital-few", label: "Vital few", prompt: "Items that drive 80% of impact." },
      { id: "trivial-many", label: "Trivial many", prompt: "Items to deprioritise." },
    ],
    output: "Pareto chart + named focus list.",
  },
  {
    id: "toc",
    name: "Theory of Constraints",
    category: "ops",
    useCase: "Bottleneck management",
    whenToUse: "Throughput improvement, supply chain.",
    inputs: [
      { id: "constraint", label: "Identify the constraint", prompt: "What limits throughput?" },
      { id: "exploit", label: "Exploit", prompt: "How to maximise output of the constraint without big investment?" },
      { id: "subordinate", label: "Subordinate", prompt: "How does everything else align to the constraint's pace?" },
      { id: "elevate", label: "Elevate", prompt: "When to invest to break the constraint?" },
      { id: "repeat", label: "Repeat", prompt: "What is the next constraint?" },
    ],
    output: "Identified constraint + four ToC steps with owners.",
  },
  {
    id: "kaizen",
    name: "Kaizen / Continuous Improvement",
    category: "ops",
    useCase: "Incremental operational excellence",
    whenToUse: "Manufacturing, service ops.",
    inputs: [
      { id: "current-state", label: "Current state", prompt: "Quantified baseline." },
      { id: "target", label: "Target state", prompt: "Quantified target with deadline." },
      { id: "experiments", label: "Small experiments", prompt: "List daily / weekly improvements with owners." },
    ],
    output: "Improvement board with measurable target.",
  },
  {
    id: "six-sigma-dmaic",
    name: "Six Sigma / DMAIC",
    category: "ops",
    useCase: "Data-driven quality improvement",
    whenToUse: "Defect reduction, process standardisation.",
    inputs: [
      { id: "define", label: "Define", prompt: "Problem, scope, customer impact." },
      { id: "measure", label: "Measure", prompt: "Baseline data and capability." },
      { id: "analyse", label: "Analyse", prompt: "Root cause hypotheses with data." },
      { id: "improve", label: "Improve", prompt: "Solutions, pilot, controls." },
      { id: "control", label: "Control", prompt: "Sustainment plan with SPC." },
    ],
    output: "DMAIC project charter with metric improvement target.",
  },
  {
    id: "lean-manufacturing",
    name: "Lean Manufacturing / Lean Enterprise",
    category: "ops",
    useCase: "Waste elimination, flow",
    whenToUse: "Operations transformation, cost reduction.",
    inputs: [
      { id: "value-stream", label: "Value stream map", prompt: "End-to-end flow + lead time + value-add %." },
      { id: "wastes", label: "Wastes (DOWNTIME)", prompt: "Defects, overproduction, waiting, non-utilised talent, transport, inventory, motion, extra-processing." },
      { id: "future-state", label: "Future state", prompt: "Target VSM with cycle time / lead time." },
    ],
    output: "Current and future-state VSMs + 3 priority wastes attacked first.",
  },
  {
    id: "make-vs-buy",
    name: "Make-vs-Buy Analysis",
    category: "ops",
    useCase: "Sourcing decisions",
    whenToUse: "Supply chain, capability decisions.",
    inputs: [
      { id: "strategic-fit", label: "Strategic fit", prompt: "Is this a core competency or commodity?" },
      { id: "cost", label: "Cost (TCO)", prompt: "Internal vs external all-in cost." },
      { id: "risk", label: "Risk & control", prompt: "IP, quality, supply security, lock-in." },
      { id: "decision", label: "Decision", prompt: "Make / buy / partner with reasoning." },
    ],
    output: "Decision memo with sensitivity to volume and time.",
  },
  {
    id: "ma-screening",
    name: "M&A Screening & Synergy",
    category: "growth",
    useCase: "Target identification, deal rationale",
    whenToUse: "Inorganic growth, due diligence.",
    inputs: [
      { id: "thesis", label: "Strategic thesis", prompt: "What problem does M&A solve that organic can't?" },
      { id: "screen", label: "Screen criteria", prompt: "Size, geo, profitability, capability, culture." },
      { id: "synergies", label: "Synergies", prompt: "Revenue + cost, with realisable %, year-over-year ramp." },
      { id: "risks", label: "Integration risks", prompt: "Top 3 deal-killers." },
    ],
    output: "1-page screen with synergy quant + integration risk.",
  },
  {
    id: "kotter-change",
    name: "Kotter's 8 Steps (Change Management)",
    category: "people",
    useCase: "Driving organisational transformation",
    whenToUse: "Large change, digital, culture.",
    inputs: [
      { id: "urgency", label: "1. Sense of urgency", prompt: "" },
      { id: "coalition", label: "2. Guiding coalition", prompt: "" },
      { id: "vision", label: "3. Vision", prompt: "" },
      { id: "communicate", label: "4. Communicate", prompt: "" },
      { id: "empower", label: "5. Empower action", prompt: "" },
      { id: "wins", label: "6. Short-term wins", prompt: "" },
      { id: "consolidate", label: "7. Consolidate gains", prompt: "" },
      { id: "anchor", label: "8. Anchor in culture", prompt: "" },
    ],
    output: "8-step plan with owners + 90-day milestones.",
  },
  {
    id: "adkar",
    name: "ADKAR (Change Adoption)",
    category: "people",
    useCase: "Individual change adoption",
    whenToUse: "Programmes touching many roles.",
    inputs: [
      { id: "awareness", label: "Awareness", prompt: "Do people know why change is happening?" },
      { id: "desire", label: "Desire", prompt: "Do they want to participate?" },
      { id: "knowledge", label: "Knowledge", prompt: "Do they know how to change?" },
      { id: "ability", label: "Ability", prompt: "Can they apply the new behaviours?" },
      { id: "reinforcement", label: "Reinforcement", prompt: "Will the change stick?" },
    ],
    output: "Per-segment ADKAR scorecard + targeted interventions.",
  },
  {
    id: "stakeholder-power-interest",
    name: "Stakeholder Power-Interest Grid",
    category: "people",
    useCase: "Influence and engagement",
    whenToUse: "Project initiation, change, coalition building.",
    inputs: [
      { id: "stakeholders", label: "Stakeholder list", prompt: "Who is affected? Who can affect?" },
      { id: "place", label: "Power × interest placement", prompt: "Manage closely / keep satisfied / keep informed / monitor." },
      { id: "actions", label: "Engagement actions", prompt: "Cadence, channel, owner per quadrant." },
    ],
    output: "Grid + 1-page engagement plan (linked: /business-analysis/stakeholder-map).",
  },
  {
    id: "raci",
    name: "RACI Matrix",
    category: "people",
    useCase: "Roles & responsibilities",
    whenToUse: "Project planning, accountability.",
    inputs: [
      { id: "tasks", label: "Tasks", prompt: "" },
      { id: "roles", label: "Roles", prompt: "" },
      { id: "rules", label: "Rules", prompt: "Exactly one A; R can be many; C consulted; I informed." },
    ],
    output: "RACI grid (linked: /project-management/raci).",
  },
  {
    id: "rapid",
    name: "Decision Rights (RAPID)",
    category: "people",
    useCase: "Who decides what",
    whenToUse: "Org design, governance, slow decisions.",
    inputs: [
      { id: "decision", label: "The decision", prompt: "What exactly is being decided?" },
      { id: "recommend", label: "Recommend (R)", prompt: "Who shapes the recommendation?" },
      { id: "agree", label: "Agree (A)", prompt: "Whose agreement is needed?" },
      { id: "perform", label: "Perform (P)", prompt: "Who executes?" },
      { id: "input", label: "Input (I)", prompt: "Who provides input?" },
      { id: "decide", label: "Decide (D)", prompt: "Single accountable decider." },
    ],
    output: "RAPID per recurring decision + escalation path.",
  },
  {
    id: "capability-maturity",
    name: "Capability Maturity Model",
    category: "transform",
    useCase: "Process maturity",
    whenToUse: "Process improvement, transformation.",
    inputs: [
      { id: "domains", label: "Domains assessed", prompt: "List domains (e.g. data, ops, people)." },
      { id: "current", label: "Current level (1-5)", prompt: "Initial / Repeatable / Defined / Managed / Optimising." },
      { id: "target", label: "Target level + deadline", prompt: "" },
      { id: "actions", label: "Bridging actions", prompt: "Investments to advance one level." },
    ],
    output: "Maturity radar + bridge plan per domain.",
  },
  {
    id: "digital-maturity",
    name: "Digital Maturity Assessment",
    category: "transform",
    useCase: "Digital readiness",
    whenToUse: "Digital transformation, tech investment.",
    inputs: [
      { id: "axes", label: "Axes", prompt: "Customer · operations · workforce · technology · culture." },
      { id: "scores", label: "Scores", prompt: "1-5 per axis, with evidence." },
      { id: "priorities", label: "Priorities", prompt: "Top 3 axes to fund this year." },
    ],
    output: "Digital maturity radar + 3 funded investments.",
  },
  {
    id: "esg-double-materiality",
    name: "ESG Double Materiality",
    category: "esg",
    useCase: "Sustainability strategy",
    whenToUse: "CSRD, comprehensive ESG.",
    inputs: [
      { id: "financial-mat", label: "Financial materiality", prompt: "Sustainability issues affecting enterprise value." },
      { id: "impact-mat", label: "Impact materiality", prompt: "Issues where the company affects people / environment." },
      { id: "intersection", label: "Intersection", prompt: "Issues that score on both axes — top priorities." },
    ],
    output: "Materiality matrix + priorities tied to disclosures.",
  },
  {
    id: "tcfd",
    name: "TCFD Climate Risk",
    category: "esg",
    useCase: "Climate-related financial disclosures",
    whenToUse: "Regulatory disclosure, scenario analysis.",
    inputs: [
      { id: "governance", label: "Governance", prompt: "Board oversight + management role." },
      { id: "strategy", label: "Strategy", prompt: "Physical + transition risks under 2 scenarios." },
      { id: "risk", label: "Risk management", prompt: "Identification, assessment, integration." },
      { id: "metrics", label: "Metrics & targets", prompt: "Scope 1/2/3 + targets." },
    ],
    output: "TCFD-aligned 1-page summary.",
  },
  {
    id: "net-zero-sbti",
    name: "Science-Based Targets / Net Zero",
    category: "esg",
    useCase: "Emissions reduction goal",
    whenToUse: "Net-zero strategy, investor expectations.",
    inputs: [
      { id: "baseline", label: "Baseline (S1, S2, S3)", prompt: "tCO2e per year, base year." },
      { id: "target", label: "Target", prompt: "% reduction by year, aligned with 1.5°C trajectory." },
      { id: "levers", label: "Decarbonisation levers", prompt: "Energy, fleet, suppliers, products." },
      { id: "offsets", label: "Residual + offsets", prompt: "Treatment of unavoidable emissions." },
    ],
    output: "SBTi-style commitment + lever-by-lever ramp.",
  },
  {
    id: "shared-value",
    name: "Shared Value (Porter & Kramer)",
    category: "esg",
    useCase: "Profit + social impact alignment",
    whenToUse: "CSR, brand differentiation.",
    inputs: [
      { id: "products", label: "Reconceiving products & markets", prompt: "" },
      { id: "value-chain", label: "Redefining productivity in the value chain", prompt: "" },
      { id: "clusters", label: "Building local clusters", prompt: "" },
    ],
    output: "Three opportunities to create shared value with metrics.",
  },
  {
    id: "good-better-best",
    name: "Good-Better-Best Pricing",
    category: "customer",
    useCase: "Tier and upsell architecture",
    whenToUse: "Product line strategy.",
    inputs: [
      { id: "good", label: "Good (entry)", prompt: "What's included? Who buys it? Conversion goal." },
      { id: "better", label: "Better (mainstream)", prompt: "What's included? Why most upgrade." },
      { id: "best", label: "Best (premium)", prompt: "Anchoring tier; what justifies premium." },
      { id: "fences", label: "Fences", prompt: "What stops better-tier customers downgrading?" },
    ],
    output: "Three-tier matrix + fences + expected mix.",
  },
  {
    id: "freemium",
    name: "Freemium Model Design",
    category: "growth",
    useCase: "Free → paid conversion",
    whenToUse: "SaaS, digital products.",
    inputs: [
      { id: "free-value", label: "Free value", prompt: "What's genuinely useful for free?" },
      { id: "limits", label: "Limits / fences", prompt: "Usage, feature, time fences for upgrade." },
      { id: "trigger", label: "Conversion trigger", prompt: "The moment the user hits the limit and upgrades." },
      { id: "support-cost", label: "Support cost ceiling", prompt: "Free user CAC + ongoing cost cap." },
    ],
    output: "Funnel with conversion-trigger moments and a free-tier cost ceiling.",
  },
  {
    id: "customer-journey",
    name: "Customer Journey Mapping",
    category: "customer",
    useCase: "End-to-end CX",
    whenToUse: "CX transformation, pain-point identification.",
    inputs: [
      { id: "stages", label: "Stages", prompt: "Awareness · consider · buy · onboard · use · advocate (or your own)." },
      { id: "actions", label: "Customer actions per stage", prompt: "" },
      { id: "feelings", label: "Feelings / pain per stage", prompt: "" },
      { id: "moments", label: "Moments of truth", prompt: "Critical interactions to win or lose the customer." },
    ],
    output: "Journey map + 3 prioritised redesigns at the moments of truth.",
  },
  {
    id: "service-blueprint",
    name: "Service Blueprint",
    category: "ops",
    useCase: "Service design",
    whenToUse: "Operational alignment, backstage processes.",
    inputs: [
      { id: "frontstage", label: "Frontstage actions", prompt: "What the customer sees and interacts with." },
      { id: "backstage", label: "Backstage actions", prompt: "Employee actions invisible to the customer." },
      { id: "support", label: "Support processes", prompt: "Systems, partners, infrastructure." },
      { id: "evidence", label: "Physical evidence", prompt: "Physical / digital touchpoints." },
    ],
    output: "Layered blueprint + redesign opportunities.",
  },
  {
    id: "kano",
    name: "Kano Model",
    category: "innovation",
    useCase: "Feature prioritisation",
    whenToUse: "Product development, satisfaction strategy.",
    inputs: [
      { id: "must", label: "Must-be (basic)", prompt: "Expected; absence dissatisfies." },
      { id: "performance", label: "Performance (linear)", prompt: "More is better; trade-off." },
      { id: "delighter", label: "Delighters", prompt: "Unexpected, drive emotional reaction." },
      { id: "indifferent", label: "Indifferent / dissatisfiers", prompt: "Cut or avoid." },
    ],
    output: "Per-feature classification + product backlog priority.",
  },
  {
    id: "jtbd",
    name: "Jobs-to-be-Done",
    category: "innovation",
    useCase: "Customer-centred innovation",
    whenToUse: "Innovation, segmentation.",
    inputs: [
      { id: "job", label: "Job statement", prompt: "When [situation], I want to [motivation], so I can [outcome]." },
      { id: "alternatives", label: "Current alternatives", prompt: "What does the customer hire today? Why is it inadequate?" },
      { id: "outcomes", label: "Desired outcomes", prompt: "Measurable, outcome-style success criteria." },
    ],
    output: "JTBD statement + outcome-based success metrics.",
  },
  {
    id: "bmc",
    name: "Business Model Canvas",
    category: "innovation",
    useCase: "Designing business models",
    whenToUse: "Startup, transformation, new venture.",
    inputs: [
      { id: "segments", label: "Customer segments", prompt: "" },
      { id: "value", label: "Value proposition", prompt: "" },
      { id: "channels", label: "Channels", prompt: "" },
      { id: "relationships", label: "Customer relationships", prompt: "" },
      { id: "revenue", label: "Revenue streams", prompt: "" },
      { id: "resources", label: "Key resources", prompt: "" },
      { id: "activities", label: "Key activities", prompt: "" },
      { id: "partners", label: "Key partners", prompt: "" },
      { id: "cost", label: "Cost structure", prompt: "" },
    ],
    output: "Single-page 9-block canvas + the riskiest assumption to test next.",
  },
  {
    id: "theory-of-change",
    name: "Theory of Change",
    category: "exec",
    useCase: "Causal logic from activity to outcome",
    whenToUse: "Programme design, social impact, evaluation.",
    inputs: [
      { id: "outcome", label: "Long-term outcome", prompt: "Ultimate change you want to see." },
      { id: "intermediate", label: "Intermediate outcomes", prompt: "Conditions required to reach the long-term outcome." },
      { id: "activities", label: "Activities & inputs", prompt: "" },
      { id: "assumptions", label: "Assumptions", prompt: "What must be true for the chain to hold?" },
    ],
    output: "Linked outcomes diagram + named assumptions to test.",
  },
  {
    id: "logic-model",
    name: "Logic Model",
    category: "exec",
    useCase: "Inputs → activities → outputs → outcomes",
    whenToUse: "Planning, evaluation, funding.",
    inputs: [
      { id: "inputs", label: "Inputs", prompt: "Resources you put in." },
      { id: "activities", label: "Activities", prompt: "What you do." },
      { id: "outputs", label: "Outputs", prompt: "Direct deliverables." },
      { id: "outcomes", label: "Outcomes (short, medium, long-term)", prompt: "Changes that result." },
    ],
    output: "Single-page logic model with measurable indicators.",
  },
  {
    id: "rfm",
    name: "RFM Analysis",
    category: "customer",
    useCase: "Customer value segmentation",
    whenToUse: "Marketing targeting, retention.",
    inputs: [
      { id: "recency", label: "Recency score (1-5)", prompt: "How recently did the customer buy?" },
      { id: "frequency", label: "Frequency score (1-5)", prompt: "How often?" },
      { id: "monetary", label: "Monetary score (1-5)", prompt: "How much value?" },
      { id: "actions", label: "Per-segment action", prompt: "Treatment for top, mid, at-risk, lost." },
    ],
    output: "RFM grid + targeted treatment per segment.",
  },
  {
    id: "ona",
    name: "Organisational Network Analysis",
    category: "people",
    useCase: "Mapping informal influence",
    whenToUse: "Transformation, M&A integration.",
    inputs: [
      { id: "data", label: "Data source", prompt: "Survey, email/meeting metadata, collaboration tools." },
      { id: "key-nodes", label: "Key nodes", prompt: "Influencers, brokers, bottlenecks, isolates." },
      { id: "actions", label: "Actions", prompt: "Who to enlist as a champion; who to unblock." },
    ],
    output: "Network map + 3 named change champions.",
  },
  {
    id: "zbb",
    name: "Zero-Based Budgeting",
    category: "finance",
    useCase: "Building budgets from scratch",
    whenToUse: "Cost transformation, accountability.",
    inputs: [
      { id: "scope", label: "ZBB scope", prompt: "Categories included." },
      { id: "drivers", label: "Cost drivers", prompt: "Activities × cost-per-unit." },
      { id: "challenge", label: "Challenge questions", prompt: "Should we do this at all? At what level?" },
      { id: "savings", label: "Targeted savings", prompt: "% per category + reallocation plan." },
    ],
    output: "Per-category ZBB build + reallocation to growth.",
  },
  {
    id: "abc",
    name: "Activity-Based Costing",
    category: "finance",
    useCase: "Allocating cost by activity",
    whenToUse: "Product profitability, service costing.",
    inputs: [
      { id: "activities", label: "Activities & cost pools", prompt: "" },
      { id: "drivers", label: "Cost drivers", prompt: "Volume drivers per activity." },
      { id: "allocation", label: "Per-product allocation", prompt: "Drivers × cost per driver." },
      { id: "implications", label: "Implications", prompt: "Loss-making products, hidden winners." },
    ],
    output: "Per-product activity cost stack + decisions.",
  },
  {
    id: "eva",
    name: "EVA / Economic Profit",
    category: "finance",
    useCase: "True economic profit",
    whenToUse: "Capital allocation, performance.",
    inputs: [
      { id: "nopat", label: "NOPAT", prompt: "Net operating profit after tax." },
      { id: "capital", label: "Invested capital", prompt: "Debt + equity used in operations." },
      { id: "wacc", label: "WACC", prompt: "Cost of capital." },
      { id: "eva", label: "EVA", prompt: "NOPAT − (Invested capital × WACC)." },
    ],
    output: "EVA per BU + capital reallocation.",
  },
  {
    id: "dupont",
    name: "DuPont Analysis",
    category: "finance",
    useCase: "Decomposing ROE",
    whenToUse: "Financial diagnostics.",
    inputs: [
      { id: "margin", label: "Net margin", prompt: "Net income / sales." },
      { id: "turnover", label: "Asset turnover", prompt: "Sales / total assets." },
      { id: "leverage", label: "Equity multiplier", prompt: "Total assets / equity." },
      { id: "roe", label: "ROE", prompt: "Margin × turnover × leverage." },
    ],
    output: "DuPont decomposition with the lever to pull.",
  },
  {
    id: "cash-conversion",
    name: "Cash Conversion Cycle",
    category: "finance",
    useCase: "Working capital",
    whenToUse: "Liquidity, cash flow improvement.",
    inputs: [
      { id: "dso", label: "DSO", prompt: "Days sales outstanding." },
      { id: "dio", label: "DIO", prompt: "Days inventory outstanding." },
      { id: "dpo", label: "DPO", prompt: "Days payable outstanding." },
      { id: "ccc", label: "CCC", prompt: "DSO + DIO − DPO." },
      { id: "actions", label: "Actions", prompt: "Per-component improvement plan." },
    ],
    output: "CCC dashboard + cash-release plan.",
  },
  {
    id: "competitive-intel",
    name: "Competitive Intelligence Map",
    category: "competitive",
    useCase: "Systematic competitor monitoring",
    whenToUse: "Strategic planning, response.",
    inputs: [
      { id: "competitors", label: "Tracked competitors", prompt: "Direct + indirect + emerging." },
      { id: "signals", label: "Signal sources", prompt: "Filings, hires, patents, pricing, ads." },
      { id: "moves", label: "Recent moves & implications", prompt: "" },
      { id: "early-warning", label: "Early-warning indicators", prompt: "What you'll watch next month." },
    ],
    output: "Quarterly CI summary + named indicators.",
  },
  {
    id: "war-game",
    name: "War Gaming",
    category: "competitive",
    useCase: "Simulating competitive dynamics",
    whenToUse: "Strategic moves, crisis prep.",
    inputs: [
      { id: "teams", label: "Teams (you, comp A, comp B, regulator, customers)", prompt: "" },
      { id: "rounds", label: "Rounds & moves", prompt: "Each team plays moves; facilitator adjudicates." },
      { id: "lessons", label: "Lessons", prompt: "Surprising threats, robust strategies, blind spots." },
    ],
    output: "Lessons-learned memo + 3 strategy adjustments.",
  },
  {
    id: "ip-strategy",
    name: "Intellectual Property Strategy",
    category: "competitive",
    useCase: "Protecting and monetising IP",
    whenToUse: "R&D, M&A, defence.",
    inputs: [
      { id: "assets", label: "IP asset map", prompt: "Patents, trademarks, copyrights, trade secrets, data." },
      { id: "freedom", label: "Freedom-to-operate", prompt: "Risks of infringement; mitigation." },
      { id: "monetisation", label: "Monetisation", prompt: "Use, license, sell, defensive." },
    ],
    output: "IP roadmap with file/maintain/abandon decisions.",
  },
];

export const getFrameworksByCategory = () => {
  const grouped = {};
  frameworkCategories.forEach((c) => { grouped[c.id] = []; });
  frameworks.forEach((f) => { (grouped[f.category] = grouped[f.category] || []).push(f); });
  return grouped;
};
